import React, {useState, useEffect} from "react"
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Textarea,
  Select,
  SelectItem,
  Chip,
  Card,
  CardBody,
  useDisclosure,
} from "@heroui/react"
import {
  IconPlus,
  IconX,
  IconInfoCircle,
  IconAlertTriangle,
  IconCheck,
} from "@tabler/icons-react"
import {
  AlertCategory,
  AlertDefinitionMetadata,
  AlertTags,
} from "@/types/database.types"
import {
  CreateAlertDefinitionInput,
  useAlertDefinitions,
} from "@/utils/hooks/useAlertDefinitions"
import {
  getAlertCategoryIcon,
  formatAlertCategoryName,
  getAlertCategoryColors,
} from "../utils/categoryUtils"
import {generateAlertName} from "../constants/alertConstants"

interface CreateAlertFormProps {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: () => void
}

export const CreateAlertForm: React.FC<CreateAlertFormProps> = ({
  isOpen,
  onOpenChange,
  onSuccess,
}) => {
  const {createAlertDefinition, creating, createError} = useAlertDefinitions()

  const defaultFormData: CreateAlertDefinitionInput = {
    name: "",
    title: "",
    description: "",
    metadata: {
      category: "user_defined",
      riskLevel: "medium",
      llmGuidance: "",
      tags: ["all_messages"],
      positiveExamples: [],
      negativeExamples: [],
    },
  }

  // Form state
  const [formData, setFormData] =
    useState<CreateAlertDefinitionInput>(defaultFormData)

  // Update generated name when title changes
  useEffect(() => {
    if (formData.title.trim()) {
      setFormData((prev) => ({
        ...prev,
        name: generateAlertName(formData.title),
      }))
    }
  }, [formData.title])

  // Individual input states for examples
  const [positiveExampleInput, setPositiveExampleInput] = useState("")
  const [negativeExampleInput, setNegativeExampleInput] = useState("")

  // Validation
  const [errors, setErrors] = useState<Record<string, string>>({})

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.title.trim()) {
      newErrors.title = "Alert title is required"
    }

    if (!formData.metadata.llmGuidance.trim()) {
      newErrors.llmGuidance = "LLM guidance is required"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleAddExample = (type: "positive" | "negative") => {
    const input =
      type === "positive" ? positiveExampleInput : negativeExampleInput
    if (!input.trim()) return

    const updatedMetadata = {
      ...formData.metadata,
      [type === "positive" ? "positiveExamples" : "negativeExamples"]: [
        ...(formData.metadata[
          type === "positive" ? "positiveExamples" : "negativeExamples"
        ] || []),
        input.trim(),
      ],
    }

    setFormData((prev) => ({
      ...prev,
      metadata: updatedMetadata,
    }))

    if (type === "positive") {
      setPositiveExampleInput("")
    } else {
      setNegativeExampleInput("")
    }
  }

  const handleRemoveExample = (
    type: "positive" | "negative",
    index: number
  ) => {
    setFormData((prev) => {
      const key = type === "positive" ? "positiveExamples" : "negativeExamples"
      const updatedExamples = (prev.metadata[key] || []).filter(
        (_, i) => i !== index
      )
      return {
        ...prev,
        metadata: {
          ...prev.metadata,
          [key]: updatedExamples,
        },
      }
    })
  }

  const handleSubmit = async () => {
    if (!validateForm()) return

    const result = await createAlertDefinition(formData)
    if (result) {
      // Reset form
      setFormData(defaultFormData)
      setErrors({})
      onSuccess?.()
      onOpenChange(false)
    }
  }

  const handleClose = () => {
    setErrors({})
    onOpenChange(false)
  }

  return (
    <Modal
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      size="4xl"
      scrollBehavior="inside"
      classNames={{
        base: "max-h-[90vh]",
        body: "py-6",
      }}
    >
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">
              <h3 className="text-xl font-semibold">Create New Alert</h3>
              <p className="text-sm font-normal text-gray-600">
                Define a new intelligent alert that will monitor your
                organization's communications
              </p>
            </ModalHeader>

            <ModalBody>
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  {/* <h4 className="text-lg font-medium">Alert Information</h4> */}

                  <div className="space-y-4">
                    <Input
                      label="Alert Title"
                      placeholder="e.g., Political Contributions"
                      value={formData.title}
                      onValueChange={(value) =>
                        setFormData((prev) => ({...prev, title: value}))
                      }
                      isInvalid={!!errors.title}
                      errorMessage={errors.title}
                      isRequired
                    />

                    {/* <Textarea
                      label="One-line Description"
                      placeholder="Brief description for the web portal (optional)"
                      value={formData.description}
                      onValueChange={(value) =>
                        setFormData((prev) => ({...prev, description: value}))
                      }
                      minRows={2}
                      description="This will be shown in the web portal's alert list to help you identify the alert"
                    /> */}
                  </div>
                </div>

                {/* LLM Guidance */}
                <div className="space-y-4">
                  {/* <h4 className="text-lg font-medium">Alert Instructions</h4> */}
                  <Textarea
                    label="Alert Guidance and Context"
                    placeholder="e.g. Flag any implications or direct mentions of donating to or aiding with political campaigns of any sort."
                    value={formData.metadata.llmGuidance}
                    onValueChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        metadata: {...prev.metadata, llmGuidance: value},
                      }))
                    }
                    minRows={4}
                    isInvalid={!!errors.llmGuidance}
                    errorMessage={errors.llmGuidance}
                    // description="Be specific about what the AI should look for and any important context"
                    isRequired
                  />
                </div>

                {/* Message Scope */}
                {/* <div className="space-y-4">
                  <h4 className="text-lg font-medium">Message Scope</h4>
                  <div className="space-y-2">
                    <p className="text-sm text-gray-600">
                      Select which types of messages this alert should monitor:
                    </p>
                    <div className="space-y-2">
                      {ALERT_TAGS.map((tag) => (
                        <label
                          key={tag}
                          className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50"
                        >
                          <input
                            type="radio"
                            name="messageScope"
                            value={tag}
                            checked={formData.messageScope === tag}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                messageScope: e.target.value as AlertTags,
                              }))
                            }
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                          />
                          <span className="text-sm font-medium">
                            {ALERT_TAG_LABELS[tag]}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div> */}

                {/* Examples Section */}
                <div>
                  <div className="space-y-4">
                    <p className="text-sm font-normal">
                      Provide a few examples that should trigger this alert
                    </p>
                    <div className="flex gap-2">
                      <Input
                        placeholder='e.g. "we should attend his fundraiser"'
                        value={positiveExampleInput}
                        onValueChange={setPositiveExampleInput}
                        onKeyDown={(
                          e: React.KeyboardEvent<HTMLInputElement>
                        ) => {
                          if (e.key === "Enter") {
                            e.preventDefault()
                            handleAddExample("positive")
                          }
                        }}
                        className="flex-1"
                      />
                      <Button
                        isIconOnly
                        color="success"
                        variant="flat"
                        onPress={() => handleAddExample("positive")}
                        isDisabled={!positiveExampleInput.trim()}
                      >
                        <IconPlus size={16} />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {formData.metadata.positiveExamples.map(
                        (example: string, index: number) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 rounded-md bg-green-50 p-2"
                          >
                            <IconCheck
                              size={16}
                              className="flex-shrink-0 text-green-600"
                            />
                            <span className="flex-1 text-sm">{example}</span>
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              onPress={() =>
                                handleRemoveExample("positive", index)
                              }
                            >
                              <IconX size={14} />
                            </Button>
                          </div>
                        )
                      )}
                    </div>
                  </div>

                  {/* Negative Examples */}
                  {/* <div className="space-y-3">
                    <h5 className="font-medium text-red-700">
                      Examples that should NOT trigger this alert
                    </h5>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Enter an example phrase that should NOT trigger this alert..."
                        value={negativeExampleInput}
                        onValueChange={setNegativeExampleInput}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault()
                            handleAddExample("negative")
                          }
                        }}
                        className="flex-1"
                      />
                      <Button
                        isIconOnly
                        color="danger"
                        variant="flat"
                        onPress={() => handleAddExample("negative")}
                        isDisabled={!negativeExampleInput.trim()}
                      >
                        <IconPlus size={16} />
                      </Button>
                    </div>
                    <div className="space-y-2">
                      {formData.metadata.negativeExamples.map(
                        (example: string, index: number) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 rounded-md bg-red-50 p-2"
                          >
                            <IconX
                              size={16}
                              className="flex-shrink-0 text-red-600"
                            />
                            <span className="flex-1 text-sm">{example}</span>
                            <Button
                              isIconOnly
                              size="sm"
                              variant="light"
                              onPress={() =>
                                handleRemoveExample("negative", index)
                              }
                            >
                              <IconX size={14} />
                            </Button>
                          </div>
                        )
                      )}
                    </div>
                  </div> */}
                </div>

                {/* Error Display */}
                {createError && (
                  <Card className="border border-red-200 bg-red-50">
                    <CardBody className="py-3">
                      <p className="text-sm text-red-800">
                        <strong>Error creating alert:</strong> {createError}
                      </p>
                    </CardBody>
                  </Card>
                )}
              </div>
            </ModalBody>

            <ModalFooter>
              <Button color="danger" variant="light" onPress={handleClose}>
                Cancel
              </Button>
              <Button
                color="primary"
                onPress={handleSubmit}
                isLoading={creating}
                isDisabled={creating || !formData.name}
              >
                Create Alert
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
